'use client';

import { useAccountUm } from '@/hooks/use-account-um';
import { useRewardsHubConfig } from '@/hooks/use-rewards/use-rewards';
import { classes } from '@/utils/classes/classes';
import {
    hasAnyFeature,
    hasFeature,
    RewardsHub,
    RewardsAppProvider,
    RewardsHubFeatures,
    RUM_TIMINGS,
    useRewardBoxLength
} from '@fanduel/rewards-hub-client';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useStore } from '@/store/store';
import { LOCAL_STORAGE, PRODUCT, ROUTES } from '@/utils/constants';
import { PlaytechGate } from '@/app/components/playtech-gate/playtech-gate';
import type { RewardsPageConfig } from '@/logic/page-config/rewards/rewards-page-config';
import { useRewardsHubAnalyticsHandlers } from '@/hooks/use-rewards/use-rewards-hub-analytics-handlers';
import type { ServerContext } from '@/store/server-context';
import { useGlobalContext } from '@/store/contexts/global-context';
import { trackRewardsHubAmplitude, getBoxAvailableStatus } from '@/tracking/amplitude/events/rewards-hub/rewards-hub';
import { REWARDS_HUB_AMP_EVENT, REWARDS_MODULE } from '@/tracking/amplitude/events/rewards-hub/event-names';
import { useMdsInstance } from '@/hooks/use-mds-instance';
import { ddRumTimingOnce } from '@/utils/datadog';

const IS_POKER_BUILD = process.env.PRODUCT === PRODUCT.POKER;

const RewardsHubGate = ({ children }: { children: ReactNode }) =>
    IS_POKER_BUILD ? <PlaytechGate>{children}</PlaytechGate> : <>{children}</>;

type RewardsHubClientPageProps = { pageConfig?: RewardsPageConfig; isLoggedIn?: boolean; userId?: string };

const RewardsHubClientPage: React.FC<RewardsHubClientPageProps> = ({ pageConfig, isLoggedIn, userId }) => {
    const { handlers, promoHandlers, sharedStateLoaded, activeCardsRef, sharedStateRef } =
        useRewardsHubAnalyticsHandlers(isLoggedIn, {
            module: REWARDS_MODULE.REWARDS_MODULE
        });
    const { isMobile, isTablet } = useGlobalContext();
    const handleShowLoading = useStore((s) => s.loading.handleShowLoading);
    const handleHideLoading = useStore((s) => s.loading.handleHideLoading);
    const sendEvent = useStore((s) => s.tracking.sendEvent);
    const sendEventRef = useRef(sendEvent);
    const rewardsHubConfig = useRewardsHubConfig(pageConfig, isLoggedIn);
    const { features } = rewardsHubConfig;
    const boxNumber = useRewardBoxLength();
    const hasRewardsHub = hasFeature(RewardsHubFeatures.HAS_REWARDS_WIDGET, features) && hasAnyFeature(features);
    const hasRewardsWidget = hasFeature(RewardsHubFeatures.HAS_REWARDS_WIDGET, features);
    const { triggerLogin, triggerRegister } = useAccountUm();
    const mdsInstance = useMdsInstance(userId);

    useEffect(() => {
        if (hasRewardsWidget && isLoggedIn) {
            window.localStorage.setItem(`${LOCAL_STORAGE.HAS_SEEN_NEW_REWARDS_PAGE}-${userId}`, 'true');
        }
    }, [hasRewardsWidget, isLoggedIn, userId]);

    useEffect(() => {
        sendEventRef.current = sendEvent;
    }, [sendEvent]);

    const hasRewardBox = hasFeature(RewardsHubFeatures.HAS_REWARDS_BOX, features);

    useEffect(() => {
        if (!isLoggedIn || !hasRewardBox || (boxNumber !== undefined && boxNumber !== -1 && sharedStateLoaded)) {
            trackRewardsHubAmplitude({
                sendEvent: sendEventRef.current,
                event: REWARDS_HUB_AMP_EVENT.REWARDS_PAGE_CONTENT_LOADED,
                sharedState: sharedStateRef.current,
                props: {
                    Module: REWARDS_MODULE.REWARDS_ACTIVE_CARDS,
                    'Rewards Box Available': getBoxAvailableStatus(boxNumber),
                    'Active Cards': activeCardsRef.current
                }
            });
        }
    }, [boxNumber, sharedStateLoaded, isLoggedIn, hasRewardBox, activeCardsRef, sharedStateRef]);

    const redirectHome = () => {
        window.location.href = ROUTES.HOMEPAGE;
    };

    const fetchOptions = useMemo(
        () => ({
            onStart: handleShowLoading,
            onEnd: () => {
                handleHideLoading();
                ddRumTimingOnce(RUM_TIMINGS.COMPONENTS_DATA_READY);
            }
        }),
        [handleShowLoading, handleHideLoading]
    );

    const promotionsFetchOptions = useMemo(
        () => ({
            onEnd: () => ddRumTimingOnce(RUM_TIMINGS.PROMOTIONS_DATA_READY)
        }),
        []
    );

    if (!hasRewardsHub) {
        return null;
    }

    return (
        <RewardsAppProvider>
            <div className="rewards-wrapper">
                <div className={classes(!isMobile && 'wrapper')}>
                    <RewardsHubGate>
                        <RewardsHub
                            config={rewardsHubConfig}
                            login={triggerLogin}
                            register={triggerRegister}
                            playCasinoBonus={redirectHome}
                            MDSInstance={mdsInstance}
                            features={features}
                            isMobileOrTablet={isMobile || isTablet}
                            isSharedStateLoaded={sharedStateLoaded}
                            fetchOptions={fetchOptions}
                            promotionsFetchOptions={promotionsFetchOptions}
                            handlers={handlers}
                            promoHandlers={promoHandlers}
                        />
                    </RewardsHubGate>
                </div>
            </div>
        </RewardsAppProvider>
    );
};

const Page = ({ pageConfig, isLoggedIn, userId }: { serverContext: ServerContext } & RewardsHubClientPageProps) => {
    return <RewardsHubClientPage pageConfig={pageConfig} isLoggedIn={isLoggedIn} userId={userId} />;
};

export default Page;
